import { useLocation } from "@reach/router";
import { intersection } from "lodash";
import PropTypes from "prop-types";
/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import React from "react";

import { useGviconfigurator } from "../hooks/useSiteConfigurator";
import { useSiteMetadata } from "../hooks/useSiteMetadata";
import StructuredData from "./structuredData";

function Seo({
  description,
  lang,
  meta,
  title,
  image,
  translations,
  children,
  eventDetails,
  courseDetails,
  blogDetails
}) {
  const { pathname, host } = useLocation();
  const site = useSiteMetadata();
  const configurator = useGviconfigurator();
  //get page locale
  const pageLocale = lang?.locale?.toLowerCase().replaceAll(/_/g, "-");
  const sitelist = configurator?.world_contact_details ?? [];

  const metaDescription = description || site?.siteMetadata?.description || "";
  const defaultTitle =
    pathname?.length > 1 ? "GVI" : site?.siteMetadata?.title ?? "";
  const metaImage = image ?? "";

  const hreflang = [];
  const canonicalUrl = host ?? "[SEO_CANONICAL_URL]";
  const paginationLinks = [];
  const sitebrand = process.env.GATSBY_PROJECT_NAV_TITLE.toLowerCase().includes(
    "people"
  )
    ? "people."
    : "www.";

  const canonicals = [
    {
      rel: `canonical`,
      href: ["https://", canonicalUrl, pathname].join("")
    }
  ];

  translations =
    Array.isArray(translations) && translations?.length > 0
      ? translations?.map((translated) => {
          let localhrefLang = translated.locale
            .toLowerCase()
            .replaceAll(/_/g, "-");
          return { ...translated, lang: localhrefLang };
        })
      : [];

  const translatedLangCodes =
    Array.isArray(translations) && translations?.length > 0
      ? translations?.map((item) => item.lang)
      : [];

  // eslint-disable-next-line no-unused-vars
  for (const [lang_code, site_detail] of Object.entries(sitelist)) {
    const langIntersection = intersection(
      translatedLangCodes,
      site_detail.lang
    );

    if (langIntersection.length > 0) {
      site_detail?.lang?.forEach((language) => {
        translations?.forEach((translated) => {
          if (langIntersection.includes(translated.lang)) {
            hreflang.push({
              rel: `alternate`,
              href: translated.href.includes("http")
                ? translated.href
                    .replace(/beta-/g, "")
                    .replace(/planet./g, "www.")
                : [
                    "https://",
                    sitebrand,
                    site_detail.domain,
                    translated.href
                  ].join(""),
              hrefLang: language
            });
          }
        });
      });
    } else if (!translatedLangCodes.includes("en-gb")) {
      site_detail?.lang?.forEach((language) => {
        if (!translatedLangCodes.includes(language)) {
          hreflang.push({
            rel: `alternate`,
            href: ["https://", sitebrand, site_detail.domain, pathname].join(
              ""
            ),
            hrefLang: language
          });
        }
      });
    } else if (translatedLangCodes.includes("en-gb")) {
      if (site_detail?.lang.includes(pageLocale)) {
        site_detail?.lang?.forEach((language) => {
          if (!translatedLangCodes.includes(language)) {
            hreflang.push({
              rel: `alternate`,
              href: ["https://", sitebrand, site_detail.domain, pathname].join(
                ""
              ),
              hrefLang: language
            });
          }
        });
      } else {
        const defaultEng = translations
          ?.filter((item) => item.lang === "en-gb")
          .shift();

        site_detail?.lang?.forEach((language) => {
          if (!translatedLangCodes.includes(language)) {
            hreflang.push({
              rel: `alternate`,
              href: defaultEng.href.includes("http")
                ? defaultEng.href
                    .replace(/beta-/g, "")
                    .replace(/planet./g, "www.")
                : [
                    "https://",
                    sitebrand,
                    site_detail.domain,
                    defaultEng.href
                  ].join(""),
              hrefLang: language
            });
          }
        });
      }
    }
  }

  //attach the x-default
  const defaultHrefPath = hreflang.filter((item) => item.hrefLang === "en-gb");

  hreflang.push({
    rel: `alternate`,
    href:
      defaultHrefPath[0] && defaultHrefPath[0]?.href
        ? defaultHrefPath[0]?.href
        : ["https://", sitebrand, "gvi.co.uk", pathname].join(""),
    hrefLang: "x-default"
  });

  return (
    <>
      <title>{`${/GVI/gi.test(title) ? title : [title, defaultTitle].filter(Boolean).join(" | ")} `}</title>
      {[
        {
          name: `description`,
          content: metaDescription
        },
        {
          property: `og:title`,
          content: title
        },
        {
          property: `og:description`,
          content: metaDescription
        },
        {
          property: `og:image`,
          content: metaImage?.sourceUrl ?? metaImage
        },
        {
          property: `og:type`,
          content: `website`
        },
        {
          name: `twitter:card`,
          content: `summary`
        },
        {
          name: `twitter:creator`,
          content: site?.siteMetadata?.author || ``
        },
        {
          name: `twitter:title`,
          content: title
        },
        {
          name: `twitter:description`,
          content: metaDescription
        }
      ]
        .concat(meta)
        .map((item, idx) => (
          <meta key={`${item.name}-${idx}`} {...item} />
        ))}

      {[]
        .concat(hreflang)
        .concat(canonicals)
        .concat(paginationLinks)
        .map((relItem, idx) => {
          return (
            <link
              key={`rel-link-${idx}`}
              rel={relItem.rel}
              href={relItem.href}
              hrefLang={relItem.hrefLang}
            />
          );
        })}
      <StructuredData
        key={"structured-data"}
        eventDetails={eventDetails}
        courseDetails={courseDetails}
        blogDetails={blogDetails}
      />
      {children}
    </>
  );
}

Seo.defaultProps = {
  lang: { locale: `en_GB` },
  meta: [],
  description: ``,
  eventDetails: false,
  courseDetails: false,
  blogDetails: false
};

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.object,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired
};

export default Seo;
